import React from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { ArrowBackOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Breadcrumb, LinkButton, LyearnBreadcrumbs } from '@lyearn/ui';
import { Link } from '@/components/Link';
import useNavigation from '@/hooks/useNavigation';

import { breadcrumbsChevronStyles, breadcrumbStyles } from './styles';

export interface BreadcrumbProps {
  to?: string;
  label: string;
  overrides?: React.ComponentProps<typeof Breadcrumb>['overrides'];
}

export interface BreadcrumbsProps {
  breadcrumbs: BreadcrumbProps[];
  className?: string;
  backLink?: string;
  backIcon?: JSX.Element;
  /** Set to false for top-level pages (e.g. sidebar destinations) that aren't a drill-down step. */
  showBackButton?: boolean;

  /* to prevent back or do something when executing */
  onBackClick?: React.MouseEventHandler;
}

export const BackButton = (props: { onClick?: () => void }) => {
  return (
    <LinkButton className="group flex-shrink-0 py-2 px-4" onClick={props.onClick}>
      <ArrowBackOutline className="fill-current text-icon-primary group-hover:text-icon-active rtl:rotate-180" />
    </LinkButton>
  );
};

const BREADCRUMB_LAST_ITEM_OVERRIDES = {
  Typography: {
    className: breadcrumbStyles,
  },
};

function Breadcrumbs(props: BreadcrumbsProps) {
  const {
    breadcrumbs,
    className = 'ps-12',
    backLink,
    backIcon,
    showBackButton = true,
    onBackClick,
  } = props;

  const navigation = useNavigation();
  const { isMobile } = useScreen();

  const BackIcon = backLink ? (
    <Link to={backLink} onClick={onBackClick ?? undefined}>
      {backIcon || <BackButton />}
    </Link>
  ) : (
    <>{backIcon || <BackButton onClick={(onBackClick as any) ?? navigation.back} />}</>
  );

  return (
    <>
      {showBackButton ? BackIcon : null}
      {isMobile ? null : (
        <LyearnBreadcrumbs className={cn(className, breadcrumbsChevronStyles)} maxItems={4}>
          {breadcrumbs.map((breadcrumb, index) => {
            const isLastItem = index === breadcrumbs.length - 1;
            const overrides =
              breadcrumb.overrides ||
              (isLastItem && !breadcrumb.to ? BREADCRUMB_LAST_ITEM_OVERRIDES : undefined);

            return (
              <Breadcrumb
                key={index}
                component={Link as any}
                overrides={overrides}
                size={isLastItem ? 'medium' : 'small'}
                to={breadcrumb.to}>
                {breadcrumb.label}
              </Breadcrumb>
            );
          })}
        </LyearnBreadcrumbs>
      )}
    </>
  );
}

/**
 * A `<Breadcrumbs showBackButton={false} />` used purely as a page title: it draws nothing on
 * mobile, so the mobile header should keep its default logo instead of an empty slot.
 */
export const isTitleOnlyBreadcrumbs = (node: React.ReactNode) =>
  React.isValidElement<BreadcrumbsProps>(node) &&
  node.type === Breadcrumbs &&
  node.props.showBackButton === false;

export default Breadcrumbs;
